import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mic, MicOff, Video as VideoIcon, VideoOff, PhoneOff,
  MessageSquare, Users, Settings, Activity as ActivityIcon,
  ChevronRight, ChevronLeft, X, Clock,
} from "lucide-react";
import { VideoCall } from "@/components/VideoCall";
import { livekitCommand } from "@/components/LiveKitCall";
import { TID } from "@/constants/testIds/aura";

/**
 * ConsultationStage — the redesigned video-consultation surface.
 *
 * Layout (mobile-first, one-thumb reachable):
 *
 *   ┌──────────────────────────────────────────┐
 *   │ ◐ HIPAA          time · role        [–]  │  <- minimal top bar
 *   │                                          │
 *   │            VIDEO FILL (Jitsi)            │
 *   │                                          │
 *   │                                          │
 *   │  ┌─── self preview (top-right) ───┐      │  (native Jitsi tile view)
 *   │                                          │
 *   │       ╭─── activity pill ───╮            │
 *   │       │ 3 new events        │  (tap →)   │
 *   │       ╰────────────────────╯            │
 *   │                                          │
 *   ├──────────────────────────────────────────┤
 *   │  [🎙][📹]      [🔴]      [💬][👥][⚙]     │  <- single bottom bar
 *   └──────────────────────────────────────────┘
 *
 * Tabbed side panel (desktop right slide-in / mobile bottom sheet):
 *   Activity · Chat · Participants · Notes (doctor only)
 *
 * The panel accepts children slots so each surface (Care / DoctorConsultation
 * / ParticipantJoin) can inject its own existing content without duplication.
 */

export function ConsultationStage({
  // Video call ------------
  roomName, displayName, email, jwt, domain,
  providerId = "livekit",
  hipaaMode = false,
  encryptionMode = "in_transit",
  knockCount = 0,           // visitors waiting at the door
  blocked = null,          // {code, message} — why the server withheld a token
  onResolveBlock,          // async () => void
  // Tab content slots (each optional) ------------
  activity,        // ReactNode — status events, always available
  chat,            // ReactNode — ClinicalMessages
  participants,    // ReactNode — ParticipantsPanel
  notes,           // ReactNode — doctor SOAP editor
  // Behaviour ------------
  autoJoin = true,
  onLeave,
  eventCount = 0,   // badge on the activity pill
  peerName,         // "Dr. Chen" or "Alex T."
  peerSubtitle,     // "Family medicine" or "Instant consult · Rm 1204"
  backTo,           // fallback route when the user leaves the stage
}) {
  const nav = useNavigate();
  const apiRef = useRef(null);
  const [audioOn, setAudioOn] = useState(true);
  const [videoOn, setVideoOn] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [openTab, setOpenTab] = useState(null); // null | 'activity' | 'chat' | 'people' | 'notes'
  const [panelSeen, setPanelSeen] = useState(false);

  // Elapsed timer ------------------------------------------------------
  useEffect(() => {
    const start = Date.now();
    const int = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(int);
  }, []);

  // Bind the LiveKit adapter surface. LiveKit's local mute state is driven
  // by the LocalParticipant hook inside ControlAdapter — the parent only
  // needs the imperative handle for the custom control bar's onClicks.
  // (Old Jitsi build subscribed to `audioMuteStatusChanged` events here;
  // LiveKit doesn't emit that event shape and the optimistic flip below
  // is good enough for a 1:1 consult.)
  const handleApiReady = (api) => {
    apiRef.current = api;
  };

  const toggleAudio = () => { livekitCommand(apiRef.current, "toggleAudio"); setAudioOn((v) => !v); };
  const toggleVideo = () => { livekitCommand(apiRef.current, "toggleVideo"); setVideoOn((v) => !v); };
  const openSettings = () => { /* LiveKit chrome is hidden — settings are driven by the app UI */ };

  // End-call is idempotent + robust to Jitsi-not-yet-joined states.
  // We always fire onLeave() ourselves after asking Jitsi to hang up. On the
  // public tenant Jitsi's `hangup` is a no-op until the user leaves the
  // pre-join card, so without this fallback the doctor would be stuck on
  // the pre-join screen after clicking End Call.
  const leftRef = useRef(false);
  const hangup = () => {
    if (leftRef.current) return;
    leftRef.current = true;
    livekitCommand(apiRef.current, "hangup");
    // Always run onLeave — the LiveKitCall's own onDisconnected will call it
    // again if it eventually fires; onLeave itself is idempotent because
    // navigation replaces the surface.
    try { onLeave?.(); } catch { /* ignore */ }
  };

  // Back button also flushes the draft (via onLeave) so the doctor never
  // loses in-flight autosave when they navigate away.
  const goBack = () => {
    try { onLeave?.(); } catch { /* ignore */ }
    nav(backTo || "/guest");
  };

  // Available tabs (skip the ones with no slot supplied) -------------
  const tabs = useMemo(() => [
    { key: "activity",     label: "Activity",     icon: ActivityIcon,     node: activity,     testid: "consult-tab-activity" },
    { key: "chat",         label: "Chat",         icon: MessageSquare,    node: chat,         testid: "consult-tab-chat" },
    { key: "people",       label: "Participants", short: "People", icon: Users, node: participants, testid: "consult-tab-people" },
    { key: "notes",        label: "Notes",        icon: Settings,         node: notes,        testid: "consult-tab-notes" },
  ].filter((t) => !!t.node), [activity, chat, participants, notes]);

  const openPanel = (key) => {
    setOpenTab(key);
    setPanelSeen(true);
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div
      className="fixed inset-0 text-white flex flex-col"
      style={{
        background: "#23342A",
      }}
      data-testid="consultation-stage"
    >
      {/* Top bar — minimal, no clutter */}
      <div className="relative z-30 flex items-center justify-between px-4 sm:px-6 pt-[env(safe-area-inset-top)]" style={{ paddingTop: "max(env(safe-area-inset-top), 12px)" }}>
        <button
          onClick={goBack}
          className="inline-flex items-center gap-1 text-white/60 text-xs uppercase tracking-widest font-semibold h-9 px-3 rounded-full hover:bg-white/8 transition-colors"
          data-testid="consult-back-btn"
          aria-label="Leave stage"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <div className="text-center min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[#5EEAD4] font-semibold">
            <span className="h-1.5 w-1.5 rounded-full bg-[#5EEAD4] pulse-dot" />
            {encryptionMode === "e2ee" ? "Live · End-to-end encrypted" : "Live · Encrypted connection"}
          </div>
          {peerName && (
            <div className="mt-0.5 font-serif text-lg sm:text-xl text-white truncate max-w-[52vw]" title={peerName}>
              {peerName}
              {peerSubtitle && <span className="text-white/55 font-sans text-sm"> · {peerSubtitle}</span>}
            </div>
          )}
        </div>
        <div className="text-xs tabular-nums text-white/75 h-9 px-3 rounded-full bg-white/8 border border-white/12 inline-flex items-center gap-1">
          <Clock className="h-3 w-3" /> {mm}:{ss}
        </div>
      </div>

      {/* Video area — fills all remaining space */}
      <div className="relative flex-1 mx-2 sm:mx-4 my-3 rounded-3xl overflow-hidden ring-1 ring-white/8 shadow-2xl">
        <VideoCall
          providerId={providerId}
          roomName={roomName}
          displayName={displayName}
          email={email}
          jwt={jwt}
          domain={domain}
          hipaaMode={hipaaMode}
          autoJoin={autoJoin}
          onLeave={onLeave}
          onApiReady={handleApiReady}
          blocked={blocked}
          onResolveBlock={onResolveBlock}
        />

        {/* Activity pill — floats bottom-center-above-controls */}
        {tabs.length > 0 && !openTab && (
          <button
            onClick={() => openPanel(tabs[0].key)}
            className="absolute left-1/2 -translate-x-1/2 bottom-4 z-20 h-10 pl-3 pr-4 rounded-full bg-white/12 border border-white/18 text-white text-sm inline-flex items-center gap-2 hover:bg-white/18 transition-colors"
            data-testid="consult-activity-pill"
          >
            <ActivityIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Activity</span>
            {eventCount > 0 && !panelSeen && (
              <span className="ml-1 min-w-[20px] h-5 rounded-full bg-[#C9995C] text-[#23291F] text-[10px] font-bold inline-flex items-center justify-center px-1.5"
                    data-testid="consult-activity-badge">
                {eventCount > 9 ? "9+" : eventCount}
              </span>
            )}
            <ChevronRight className="h-3.5 w-3.5 opacity-60" />
          </button>
        )}
      </div>

      {/* Single bottom control bar — one-thumb reachable */}
      <div
        className="relative z-30 px-4 pb-[max(env(safe-area-inset-bottom),16px)]"
      >
        <div
          className="mx-auto max-w-lg rounded-full bg-white/10 border border-white/15 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] px-4 py-3 flex items-center justify-between gap-2"
          role="toolbar"
          aria-label="Consultation controls"
          data-testid="consult-controls-bar"
        >
          {/* Primary group — mic + camera */}
          <div className="flex items-center gap-2">
            <ControlButton
              onClick={toggleAudio}
              active={audioOn}
              activeLabel="Mute microphone"
              inactiveLabel="Unmute microphone"
              iconOn={<Mic className="h-5 w-5" />}
              iconOff={<MicOff className="h-5 w-5" />}
              testid={TID.micBtn}
            />
            <ControlButton
              onClick={toggleVideo}
              active={videoOn}
              activeLabel="Turn camera off"
              inactiveLabel="Turn camera on"
              iconOn={<VideoIcon className="h-5 w-5" />}
              iconOff={<VideoOff className="h-5 w-5" />}
              testid={TID.camBtn}
            />
          </div>

          {/* Primary — end call (center, largest, destructive) */}
          <button
            onClick={hangup}
            className="h-14 w-14 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center shadow-[0_10px_30px_-6px_rgba(220,38,38,0.55)] transition-all active:scale-95 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2 focus:ring-offset-[#052E2B]"
            aria-label="End call"
            data-testid={TID.endCall}
          >
            <PhoneOff className="h-6 w-6" />
          </button>

          {/* Secondary group — chat / participants / settings */}
          <div className="flex items-center gap-2">
            {chat && (
              <ControlButton
                onClick={() => openPanel("chat")}
                active={openTab === "chat"}
                activeLabel="Close chat"
                inactiveLabel="Open chat"
                iconOn={<MessageSquare className="h-5 w-5" />}
                iconOff={<MessageSquare className="h-5 w-5" />}
                testid="consult-chat-btn"
              />
            )}
            {participants && (
              <div className="relative">
                <ControlButton
                  onClick={() => openPanel("people")}
                  active={openTab === "people"}
                  activeLabel="Close participants"
                  inactiveLabel="Open participants"
                  iconOn={<Users className="h-5 w-5" />}
                  iconOff={<Users className="h-5 w-5" />}
                  testid="consult-people-btn"
                />
                {knockCount > 0 && openTab !== "people" && (
                  <span
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D69E2E] text-[#201D17] text-[10px] font-bold flex items-center justify-center knock-pulse"
                    data-testid="consult-knock-badge"
                  >
                    {knockCount}
                  </span>
                )}
              </div>
            )}
            <ControlButton
              onClick={openSettings}
              active={false}
              activeLabel="Settings"
              inactiveLabel="Settings"
              iconOn={<Settings className="h-5 w-5" />}
              iconOff={<Settings className="h-5 w-5" />}
              testid="consult-settings-btn"
            />
          </div>
        </div>
        <p className="text-center text-white/45 text-[11px] mt-2 leading-relaxed">
          Secure video · Please do not record or screenshot this consultation.
        </p>
      </div>

      {/* Side / bottom-sheet panel with tabs */}
      {openTab && (
        <SidePanel
          tabs={tabs}
          openTab={openTab}
          onTabChange={setOpenTab}
          onClose={() => setOpenTab(null)}
        />
      )}
    </div>
  );
}

/* ------------------------------- helpers ------------------------------- */
function ControlButton({ onClick, active, activeLabel, inactiveLabel, iconOn, iconOff, testid }) {
  return (
    <button
      onClick={onClick}
      aria-label={active ? activeLabel : inactiveLabel}
      aria-pressed={active}
      data-testid={testid}
      className={`h-12 w-12 rounded-full flex items-center justify-center transition-all active:scale-95 focus:outline-none focus:ring-2 focus:ring-white/30 ${
        active
          ? "bg-white/15 text-white hover:bg-white/25"
          : "bg-white text-[#052E2B] hover:bg-white/90"
      }`}
    >
      {active ? iconOn : iconOff}
    </button>
  );
}

function SidePanel({ tabs, openTab, onTabChange, onClose }) {
  const active = tabs.find((t) => t.key === openTab) || tabs[0];
  return (
    <>
      {/* Backdrop (mobile only) */}
      <div
        className="fixed inset-0 bg-black/40 z-40 sm:hidden"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="fixed z-50 bg-white text-ink shadow-2xl
                   inset-x-0 bottom-0 max-h-[85vh] rounded-t-3xl
                   sm:inset-x-auto sm:right-0 sm:top-0 sm:bottom-0
                   sm:w-[400px] sm:max-h-none sm:rounded-none sm:border-l sm:border-[rgba(100,116,139,0.16)]
                   flex flex-col"
        role="dialog"
        aria-label={`${active.label} panel`}
        data-testid="consult-panel"
      >
        {/* Handle bar (mobile) */}
        <div className="sm:hidden pt-3 pb-1 flex justify-center">
          <div className="h-1.5 w-10 rounded-full bg-[rgba(100,116,139,0.28)]" />
        </div>

        {/* Tab strip + close */}
        <div className="flex items-center justify-between gap-2 px-3 sm:px-4 pt-2 sm:pt-4 pb-3 border-b border-[rgba(100,116,139,0.14)]">
          <div className="flex items-center gap-1 overflow-x-auto -mx-1 px-1 hide-scrollbar min-w-0 flex-1">
            {tabs.map((t) => {
              const Icon = t.icon;
              const isActive = t.key === openTab;
              return (
                <button
                  key={t.key}
                  onClick={() => onTabChange(t.key)}
                  data-testid={t.testid}
                  aria-pressed={isActive}
                  className={`h-9 px-3 rounded-full text-xs uppercase tracking-widest font-semibold inline-flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-brand text-white"
                      : "text-denim hover:text-brand hover:bg-brand-soft"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t.short || t.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-denim hover:bg-[rgba(100,116,139,0.10)]"
            aria-label="Close panel"
            data-testid="consult-panel-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Active tab content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6" data-testid={`consult-panel-content-${active.key}`}>
          {active.node}
        </div>
      </aside>
    </>
  );
}

/* -------------------------- Activity helper -------------------------- */
/**
 * ActivityFeed — canonical "single place" for status updates / waiting-room
 * messages / event notifications. Consumers pass an array of items:
 *   { id, at, kind: 'status'|'waiting'|'event'|'system', title, body? }
 *
 * The stage renders this inside the Activity tab. Grouping all events here
 * satisfies the spec: no scattered banners, one collapsible panel, one place
 * the user always knows where to look.
 */
export function ActivityFeed({ items = [] }) {
  if (items.length === 0) {
    return (
      <div className="text-center text-denim text-sm py-10" data-testid="activity-empty">
        No activity yet. Status updates will appear here.
      </div>
    );
  }
  return (
    <ul className="space-y-3" data-testid="activity-feed">
      {items.map((it) => (
        <li key={it.id} className="rounded-2xl border border-[rgba(100,116,139,0.14)] p-3 bg-white" data-testid={`activity-item-${it.kind}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-denim font-semibold">{it.kind}</div>
              <div className="text-sm text-ink mt-0.5">{it.title}</div>
              {it.body && <div className="text-xs text-denim mt-1 leading-relaxed">{it.body}</div>}
            </div>
            <div className="shrink-0 text-[11px] text-denim/70 tabular-nums">
              {it.at ? new Date(it.at).toLocaleTimeString() : ""}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
